// TODO: delete when new customize UI enabled for everyone
import PropTypes from 'prop-types';
import { action, extendObservable, reaction } from 'mobx';
import { apiCall } from '../API';
import AsyncActionStore from './AsyncActionStore';
import FlagStore from './FlagStore';
import InputStore from './InputStore';
import RadioButtonStore from './RadioButtonStore';
import { BrandingOption, HelpButton, HelpButtonAction } from './BrowserBarOptions';

export default class BrowserBarOptionsStore {
    static PropType = PropTypes.shape({
        loadStatus: AsyncActionStore.PropType.isRequired,
        updateStatus: AsyncActionStore.PropType.isRequired,
        load: PropTypes.func.isRequired,
        update: PropTypes.func.isRequired,
    });

    constructor(data = { }) {
        this.customBrandingTextMaxLength = 32;

        extendObservable(this, /** @class BrowserBarOptionsStore */{
            apiLoad: apiCall('/console/browser_bar/get'),
            apiUpdate: apiCall('/console/browser_bar/update'),
            loadStatus: new AsyncActionStore(),
            updateStatus: new AsyncActionStore(),

            pagePreviewToggle: new FlagStore(true),
            editModal: new FlagStore(false),
            exitConfirmationModal: new FlagStore(false),
            modalPreviewToggle: new FlagStore(true),

            brandingOption: BrandingOption.DEFAULT,
            brandingOptionStore: new RadioButtonStore(BrandingOption.DEFAULT),
            customBrandingText: '',
            customBrandingTextStore: new InputStore(),
            isCustomBrandingEditable: () => this.brandingOptionStore.equals(BrandingOption.CUSTOM),
            helpButton: HelpButton.DISABLED,
            helpButtonStore: new RadioButtonStore(HelpButton.DISABLED),
            helpButtonAction: HelpButtonAction.LINK,
            helpButtonActionStore: new RadioButtonStore(HelpButtonAction.LINK),
            externalLink: '',
            externalLinkStore: new InputStore(),
            isExternalLinkEditable: () => !this.helpButtonStore.equals(HelpButton.DISABLED)
                && this.helpButtonActionStore.equals(HelpButtonAction.LINK),
            popupMessage: '',
            popupMessageStore: new InputStore(),
            isPopupMessageEditable: () => !this.helpButtonStore.equals(HelpButton.DISABLED)
                && this.helpButtonActionStore.equals(HelpButtonAction.POPUP),

            hasChanged() {
                return !this.brandingOptionStore.equals(this.brandingOption)
                    || !this.customBrandingTextStore.equals(this.customBrandingText)
                    || !this.helpButtonStore.equals(this.helpButton)
                    || !this.helpButtonActionStore.equals(this.helpButtonAction)
                    || !this.externalLinkStore.equals(this.externalLink)
                    || !this.popupMessageStore.equals(this.popupMessage);
            },

            isValid() {
                return (!this.isCustomBrandingEditable() || this.customBrandingTextStore.isValid)
                    && (!this.isExternalLinkEditable() || this.externalLinkStore.isValid)
                    && (!this.isPopupMessageEditable() || this.popupMessageStore.isValid);
            },
        }, data);

        this.customBrandingTextStore.setValidator((value) => {
            if (!value) {
                return false;
            }
            if (value.length > this.customBrandingTextMaxLength) {
                return `Custom branding cannot exceed ${this.customBrandingTextMaxLength} characters.`;
            }
            return true;
        });

        this.externalLinkStore.setValidator((value) => {
            if (!value) {
                return false;
            }
            try {
                const url = new window.URL(value);
                if (url.protocol === 'http:' || url.protocol === 'https:') {
                    return true;
                }
            } catch (e) { /* whatever */ }

            return 'URL is invalid';
        });

        this.popupMessageStore.setValidator((value) => !!value);

        // focus custom branding text after custom branding option selected
        reaction(() => this.editModal.isOpen
                && this.brandingOptionStore.equals(BrandingOption.CUSTOM),
        (cond) => cond && setTimeout(this.customBrandingTextStore.focus));

        // select external link input when it shows up
        reaction(() => this.editModal.isOpen
            && !this.helpButtonStore.equals(HelpButton.DISABLED)
            && this.helpButtonActionStore.equals(HelpButtonAction.LINK),
        (cond) => cond && setTimeout(this.externalLinkStore.focus));

        // select popup editor when it shows up
        reaction(() => this.editModal.isOpen
            && !this.helpButtonStore.equals(HelpButton.DISABLED)
            && this.helpButtonActionStore.equals(HelpButtonAction.POPUP),
        (cond) => cond && setTimeout(this.popupMessageStore.focus));

        // select "no link" help link option if custom branding was a link option and user switched to "default branding"
        reaction(() => this.editModal.isOpen
            && this.brandingOptionStore.equals(BrandingOption.DEFAULT)
            && this.helpButtonStore.equals(HelpButton.BRANDING),
        (cond) => cond && this.helpButtonStore.setValue(HelpButton.DISABLED));
    }

    setValue = action((data) => {
        this.brandingOption = data.brandingOption;
        this.brandingOptionStore.setValue(data.brandingOption);
        this.customBrandingText = data.customBrandingText;
        this.customBrandingTextStore.setValue(data.customBrandingText);
        this.customBrandingTextStore.setTouched(false);
        this.helpButton = data.helpButton;
        this.helpButtonStore.setValue(data.helpButton);
        this.helpButtonAction = data.helpButtonAction;
        this.helpButtonActionStore.setValue(data.helpButtonAction);
        this.externalLink = data.externalLink;
        this.externalLinkStore.setValue(data.externalLink);
        this.externalLinkStore.setTouched(false);
        this.popupMessage = data.popupMessage;
        this.popupMessageStore.setValue(data.popupMessage);
        this.popupMessageStore.setTouched(false);
    });

    getValue() {
        return {
            brandingOption: this.brandingOptionStore.getValue(),
            customBrandingText: this.customBrandingTextStore.getValue(),
            helpButton: this.helpButtonStore.getValue(),
            helpButtonAction: this.helpButtonActionStore.getValue(),
            externalLink: this.externalLinkStore.getValue(),
            popupMessage: this.popupMessageStore.getValue(),
        };
    }

    load = action(() => {
        if (!this.loadStatus.isLoading) {
            this.loadStatus.setLoading(this.apiLoad().then(this.setValue));
        }
        return this.loadStatus.promise;
    });

    update = action(() => {
        if (!this.updateStatus.isLoading) {
            if (this.isCustomBrandingEditable()) {
                this.customBrandingTextStore.setTouched(true);
                if (!this.customBrandingTextStore.isValid) {
                    this.customBrandingTextStore.focus();
                    return Promise.reject();
                }
            } else {
                this.customBrandingTextStore.reset();
            }

            if (this.isExternalLinkEditable()) {
                this.externalLinkStore.setTouched(true);
                if (!this.externalLinkStore.isValid) {
                    this.externalLinkStore.focus();
                    return Promise.reject();
                }
            } else {
                this.externalLinkStore.reset();
            }

            if (this.isPopupMessageEditable()) {
                this.popupMessageStore.setTouched(true);
                if (!this.popupMessageStore.isValid) {
                    this.popupMessageStore.focus();
                    return Promise.reject();
                }
            } else {
                this.popupMessageStore.reset();
            }

            this.updateStatus.setLoading(this.apiUpdate(this.getValue()))
                .then(action((resp) => {
                    this.editModal.close();
                    this.setValue(resp);
                }));
        }
        return this.updateStatus.promise;
    });

    openEditModal = action(() => {
        this.setValue(this);
        this.editModal.open();
    });

    closeEditModal = action(() => {
        if (this.hasChanged()) {
            this.exitConfirmationModal.open();
        } else {
            this.editModal.close();
        }
    });
}
